import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  Bot,
  ChevronRight,
  Plus,
  Bolt,
  FileDown,
  FileUp,
  BotMessageSquare,
  Loader2,
  Wrench,
  Table,
} from "lucide-react";

import { auth } from "../../firebase";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarSeparator,
} from "../../components/ui/sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";
import { useHazardGenerator } from "../../components/hooks/useHazardGenerator";

import { CommonDialog } from "../../components/CommonDialog";
import RiskMatrixSelectionModal from "../../components/Risk Matrix/RiskMatrixSelectionModal";
import HazardExcelImport from "../../components/HazardExcelImport";

import functionsService from "../../services/functionsService";
import firestoreService from "../../services/firestoreService";

let MIL882EMatrix, SampleMatrix;
(async () => {
  // Load risk matrices when module is initialized
  const riskMatrices = await import("../../components/Risk Matrix/riskMatrixShared");
  MIL882EMatrix = riskMatrices.MIL882EMatrix;
  SampleMatrix = riskMatrices.SampleMatrix;
})().catch(console.error);

const HazardSidebar = () => {
  const [isOwner, setIsOwner] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [hazards, setHazards] = useState([]);
  const [riskMatrix, setRiskMatrix] = useState(null);
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);

  const [showHazardGenerationWarning, setShowHazardGenerationWarning] =
    useState(false);

  const [selectedAiModel, setSelectedAiModel] = useAiModel();
  const [isGeneratingHazards, setIsGeneratingHazards] =
    useHazardGenerator(false);

  const [isExporting, setIsExporting] = useState(false);

  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchProject = useCallback((projectId) => {
    const unsubscribe = firestoreService.subscribeToItem(
      "projects",
      projectId,
      [],
      (project) => {
        if (project) {
          setActiveProject(project);
          setIsOwner(project.roles.owners.includes(auth.currentUser.uid));
          setIsEditor(project.roles.editors.includes(auth.currentUser.uid));
        }
      }
    );

    return () => unsubscribe;
  }, []);

  const fetchHazards = useCallback((projectId) => {
    const unsubscribe = firestoreService.subscribeToCollection(
      `projects/${projectId}/hazards`,
      [],
      (hazards) => {
        hazards.sort((a, b) => {
          return a.hazardId < b.hazardId ? -1 : a.hazardId > b.hazardId ? 1 : 0;
        });

        setHazards(hazards);
      }
    );

    // Cleanup the listener on component unmount
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!location.pathname.includes("hazards")) {
      setActiveProject(null);
      return;
    }

    const projectId = location.pathname.split("/")[2];

    fetchProject(projectId);
    fetchHazards(projectId);
  }, [location, fetchProject, fetchHazards]);

  useEffect(() => {
    const loadRiskMatrix = async () => {
      if (activeProject && activeProject.riskMatrix) {
        try {
          // First check if the matrix is a standard one
          if (activeProject.riskMatrix === "Sample Matrix") {
            console.log("Using built-in Sample Matrix definition");
            setRiskMatrix(SampleMatrix);
            return;
          }

          if (activeProject.riskMatrix === "MIL-882E") {
            console.log("Using built-in MIL-882E Matrix definition");
            setRiskMatrix(MIL882EMatrix);
            return;
          }

          // If not a standard matrix, try to get from Firestore
          const matrix = await firestoreService.get("riskHelper", activeProject.riskMatrix);

          if (matrix) {
            // Ensure matrix is in the correct format
            const formattedMatrix = {
              ...matrix,
              matrix: typeof matrix.matrix === "string"
                ? matrix.matrix.split(";").map(row => row.split("|"))
                : matrix.matrix,
              descriptions: matrix.descriptions || {
                probabilities: {},
                severities: {},
              }
            };
            setRiskMatrix(formattedMatrix);
          } else {
            console.warn(
              `Risk matrix ${activeProject.riskMatrix} not found, using default`
            );
            // Only use default as a last resort
            setRiskMatrix(MIL882EMatrix);
          }
        } catch (error) {
          console.error("Error loading risk matrix:", error);

          // If we failed to load a matrix, use the built-in ones as fallback
          if (activeProject.riskMatrix === "Sample Matrix") {
            console.log("Error loading from database, using built-in Sample Matrix");
            setRiskMatrix(SampleMatrix);
          } else if (activeProject.riskMatrix === "MIL-882E") {
            console.log("Error loading from database, using built-in MIL-882E Matrix");
            setRiskMatrix(MIL882EMatrix);
          } else {
            toast({
              variant: "destructive",
              title: "Error loading risk matrix",
              description: "Failed to load risk matrix. Using default matrix instead.",
            });

            // Fallback to default matrix
            setRiskMatrix(MIL882EMatrix);
          }
        }
      } else if (activeProject) {
        console.warn("No risk matrix specified for project, using default");
        setRiskMatrix(MIL882EMatrix);
      }
    };

    loadRiskMatrix();
  }, [activeProject, toast]);

  const generateHazards = () => {
    if (hazards.length > 0) {
      setShowHazardGenerationWarning(true);
    } else {
      doGenerateHazards();
    }
  };

  const doGenerateHazards = useCallback(async () => {
    setIsGeneratingHazards(true);

    try {
      const { success } = await functionsService.generatePotentialHazards(
        activeProject.id,
        selectedAiModel
      );

      if (!success) {
        console.error("Failed to generate potential hazards.");

        toast({
          variant: "destructive",
          title: "Failed to generate potential hazards.",
        });
      }
    } catch (error) {
      console.error("Failed to generate potential hazards.", error);

      toast({
        variant: "destructive",
        title: "Failed to generate potential hazards.",
        description: error.message,
      });
    } finally {
      setIsGeneratingHazards(false);
    }
  }, [activeProject, selectedAiModel, setIsGeneratingHazards, toast]);

  // Placeholder function for matrix cell selection
  const handleCellSelect = (probability, severity) => {
    console.log(`Selected: Probability ${probability}, Severity ${severity}`);
  };

  // Handle refreshing the hazard list after importing
  const handleImportComplete = useCallback(() => {
    if (activeProject) {
      // Refetch hazards for the current project
      fetchHazards(activeProject.id);
    }
  }, [activeProject, fetchHazards]);

  // Import Excel modal state
  const [showImportModal, setShowImportModal] = useState(false);

  return (
    <>
      <SidebarSeparator className="mt-2" />

      <SidebarGroup>
        <SidebarGroupLabel>Project</SidebarGroupLabel>

        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                disabled={!isOwner && !isEditor}
                tooltip="Project properties"
                onClick={() => {
                  navigate(`/projects/${activeProject.id}/properties`);
                }}
              >
                <div>
                  <Wrench />
                  <span>Project properties</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                disabled={!isOwner && !isEditor}
                tooltip="Project context"
                onClick={() => {
                  navigate(`/projects/${activeProject.id}/context`);
                }}
              >
                <div>
                  <Bolt />
                  <span>Project context</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Risk Matrix"
                onClick={() => {
                  setIsRiskMatrixModalOpen(true);
                }}
              >
                <div>
                  <Table className="h-4 w-4 mr-2" />
                  <span>Risk Matrix</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>Hazards</SidebarGroupLabel>

        <SidebarGroupContent>
          <SidebarMenu>
            {/* <SidebarMenuItem> //TODO: update to use Add hazard similar to the one in HazardList
              <HazardModal
                isOpen={hazardEditorModalOpen}
                setIsOpen={setHazardEditorModalOpen}
              >
                <EditHazardForm
                  project={activeProject}
                  setIsModalOpen={setHazardEditorModalOpen}
                />
              </HazardModal>

              <Link to={`/projects/${activeProject?.id}/hazards/add`}>
                <SidebarMenuButton
                  asChild
                  disabled={
                    (activeProject && !activeProject.context) ||
                    (!isOwner && !isEditor)
                  }
                  tooltip="Add hazard"
                >
                  <div>
                    <Plus />
                    <span>Add hazard</span>
                  </div>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>

            <SidebarSeparator className="my-1" /> */}

            <SidebarMenuItem>
              <CommonDialog
                open={showHazardGenerationWarning}
                setOpen={setShowHazardGenerationWarning}
                title={"Warning: Duplicate Hazards"}
                description={
                  "Generating hazards when hazards already exist may create duplicates. Are you sure you want to proceed?"
                }
                onConfirm={() => {
                  doGenerateHazards();
                }}
              />

              <SidebarMenuButton
                asChild
                disabled={
                  isGeneratingHazards ||
                  (activeProject && !activeProject.context) ||
                  (!isOwner && !isEditor)
                }
                tooltip="Generate hazards"
                onClick={generateHazards}
              >
                <div>
                  {isGeneratingHazards ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}

                  {isGeneratingHazards ? (
                    <span>Generating...</span>
                  ) : (
                    <span>Generate hazards</span>
                  )}
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <Collapsible asChild>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  disabled={!isOwner && !isEditor}
                  tooltip="Models"
                >
                  <div>
                    <BotMessageSquare />
                    <span>Models</span>
                  </div>
                </SidebarMenuButton>

                <CollapsibleTrigger asChild>
                  <SidebarMenuAction
                    disabled={!isOwner && !isEditor}
                    className="data-[state=open]:rotate-90"
                  >
                    <ChevronRight />
                    <span className="sr-only">Toggle</span>
                  </SidebarMenuAction>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarMenuSub>
                    <RadioGroup
                      value={selectedAiModel}
                      onValueChange={setSelectedAiModel}
                      disabled={!isOwner && !isEditor}
                      defaultValue="gpt-4o"
                    >
                      <div className="flex items-center space-x-2 mx-1 mt-1 mb-1">
                        <RadioGroupItem value="gpt-4o" id="gpt-4o" />
                        <Label htmlFor="gpt-4o">GPT-4o</Label>
                      </div>

                      <div className="flex items-center space-x-2 mx-1 mt-1 mb-1">
                        <RadioGroupItem
                          value="claude-3-7-sonnet-20250219"
                          id="claude-3-7-sonnet-20250219"
                        />
                        <Label htmlFor="claude-3-7-sonnet-20250219">
                          Claude 3.7 Sonnet
                        </Label>
                      </div>
                    </RadioGroup>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <SidebarSeparator className="my-1" />

            <SidebarMenuItem>
              <SidebarMenuButton 
                asChild
                disabled={!activeProject}
                tooltip="Import from Excel"
                onClick={() => setShowImportModal(true)}
              >
                <div>
                  <FileDown />
                  <span>Import from Excel</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                disabled={!activeProject || !hazards.length || isExporting}
                tooltip="Export to Excel"
                onClick={() => {
                  if (activeProject && hazards.length > 0) {
                    setIsExporting(true);
                    
                    try {
                      // Use XLSX directly for export
                      import('xlsx').then(XLSX => {
                        const projectName = activeProject?.name || "Project";
                        
                        // Format the hazards data for Excel
                        const hazardRows = hazards.map(hazard => ({
                          "_firestoreId": hazard.id, // Add Firestore ID (hidden field prefixed with underscore)
                          "Hazard ID": `${hazard.hazardId}`,
                          "Description": hazard.description || "",
                          "Hazard Type": hazard.type || "",
                          "Hazard Source": hazard.hazardSource || "",
                          "Risk Category": (hazard.riskCategory || []).join(", "),
                          "Risk Status": hazard.riskStatus || "",
                          "Systems": (hazard.systems || []).join(", "),
                          "Operating Modes": (hazard.operatingModes || []).join(", "),
                          "Locations": (hazard.locations || []).join(", "),
                          "Project Lifecycles": (hazard.projectLifecycles || []).join(", "),
                          "Disciplines": (hazard.disciplines || []).join(", "),
                          "Drawing Packages": (hazard.drawingPackages || []).join(", "),
                          "Users": (hazard.users || []).join(", "),
                          "SFAIRP Statement": hazard.sfairpStatement || "",
                          "Unmitigated Risk Probability": hazard.unmitigatedRisk?.probability || "",
                          "Unmitigated Risk Severity": hazard.unmitigatedRisk?.severity || "",
                          "Unmitigated Risk Rating": hazard.unmitigatedRisk?.rating || "",
                          "Retained Risk Probability": hazard.retainedRisk?.probability || "",
                          "Retained Risk Severity": hazard.retainedRisk?.severity || "",
                          "Retained Risk Rating": hazard.retainedRisk?.rating || "",
                        }));
                        
                        // Create mitigations data
                        const mitigationRows = hazards.flatMap(hazard => 
                          (hazard.mitigations || []).map(mitigation => ({
                            "_hazard_firestoreId": hazard.id, // Add hazard Firestore ID (hidden field)
                            "_internal_id": mitigation.id, // Add internal UUID (hidden field)
                            "Hazard ID": hazard.hazardId,
                            "Mitigation ID": mitigation.mitigationId || "",
                            "Type": mitigation.type || "",
                            "Description": mitigation.description || "",
                            "Owner": mitigation.owner || "",
                            "Status": mitigation.status || "",
                            "Due Date": mitigation.dueDate || "",
                            "Safety Requirement": mitigation.safetyRequirement || "",
                            "Evidence": mitigation.evidence || ""
                          }))
                        );
                        
                        // Create workbook and sheets
                        const wb = XLSX.utils.book_new();
                        
                        // Add hazards sheet
                        const hazardsWS = XLSX.utils.json_to_sheet(hazardRows);
                        XLSX.utils.book_append_sheet(wb, hazardsWS, "Hazards");
                        
                        // Add mitigations sheet
                        const mitigationsWS = XLSX.utils.json_to_sheet(mitigationRows);
                        XLSX.utils.book_append_sheet(wb, mitigationsWS, "Mitigations");
                        
                        // Write file
                        const fileName = `${projectName}_Hazards_${new Date().toISOString().split("T")[0]}.xlsx`;
                        XLSX.writeFile(wb, fileName);
                        
                        toast({
                          title: "Export successful",
                          description: `Hazards have been exported to ${fileName}`,
                        });
                      });
                    } catch (error) {
                      console.error("Export error:", error);
                      
                      toast({
                        variant: "destructive",
                        title: "Export failed",
                        description: error.message,
                      });
                    } finally {
                      setIsExporting(false);
                    }
                  }
                }}
              >
                <div>
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileUp />
                  )}
                  
                  {isExporting ? (
                    <span>Exporting...</span>
                  ) : (
                    <span>Export to Excel</span>
                  )}
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <RiskMatrixSelectionModal
        isOpen={isRiskMatrixModalOpen}
        setIsOpen={setIsRiskMatrixModalOpen}
        riskMatrix={riskMatrix}
        onSelectCell={handleCellSelect}
        title="Risk Matrix"
        description={`Risk matrix for ${activeProject?.name || "the current project"}`}
      />

      {/* Import Dialog */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Hazards from Excel</DialogTitle>
            <DialogDescription>
              Upload an Excel file to import hazards and mitigations
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {activeProject && (
              <HazardExcelImport
                project={activeProject}
                hazards={hazards}
                onImportComplete={() => {
                  handleImportComplete();
                  setShowImportModal(false);
                }}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HazardSidebar;
